import * as Cesium from "cesium";

export function getEntitiesWithOverlappingPolylines(viewer, currentDataSource) {
  if (!currentDataSource || !currentDataSource.entities) return [];

  const ellipsoid = (viewer && viewer.scene && viewer.scene.globe && viewer.scene.globe.ellipsoid) || Cesium.Ellipsoid.WGS84;

  const entities = currentDataSource.entities.values || currentDataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) return [];

  const time = (viewer && viewer.clock && viewer.clock.currentTime) || Cesium.JulianDate.now();

  // properties.id → fallback entity.id
  function getEntityId(entity) {
    if (!entity) return undefined;
    const props = entity.properties;
    let idFromProps;

    if (props) {
      if (typeof props.get === "function") {
        const property = props.get("id");
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
        const property = props._properties.id;
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && typeof props.getValue === "function") {
        const obj = props.getValue();
        if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
          idFromProps = obj.id;
        }
      }

      if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
        idFromProps = props.id;
      }
    }

    if (idFromProps !== undefined && idFromProps !== null) return idFromProps;
    return entity.id;
  }

  function getPolylinePositions(entity, time) {
    if (!entity || !entity.polyline) return null;

    const polyline = entity.polyline;
    const positionsProp = polyline.positions || polyline._positions;
    if (!positionsProp) return null;

    let positions = null;

    if (typeof positionsProp.getValue === "function") {
      positions = positionsProp.getValue(time);
    } else if (Array.isArray(positionsProp)) {
      positions = positionsProp;
    } else if (Array.isArray(positionsProp._value)) {
      positions = positionsProp._value;
    }

    if (!Array.isArray(positions) || positions.length === 0) return null;
    return positions;
  }

  function buildPolylineSignature(positions) {
    if (!positions || positions.length === 0) return null;

    const PRECISION = 6; // ~0.1 m
    const forward = [];
    const reverse = [];

    for (let i = 0; i < positions.length; i++) {
      const cart = positions[i];
      if (!cart) return null;
      const c = ellipsoid.cartesianToCartographic(cart);
      if (!c) return null;

      const lat = Cesium.Math.toDegrees(c.latitude).toFixed(PRECISION);
      const lon = Cesium.Math.toDegrees(c.longitude).toFixed(PRECISION);
      forward.push(lat + "," + lon);
    }

    for (let i = positions.length - 1; i >= 0; i--) {
      const cart = positions[i];
      if (!cart) return null;
      const c = ellipsoid.cartesianToCartographic(cart);
      if (!c) return null;

      const lat = Cesium.Math.toDegrees(c.latitude).toFixed(PRECISION);
      const lon = Cesium.Math.toDegrees(c.longitude).toFixed(PRECISION);
      reverse.push(lat + "," + lon);
    }

    const s1 = forward.join(";");
    const s2 = reverse.join(";");

    return s1 < s2 ? s1 : s2;
  }

  const candidates = [];

  for (let i = 0; i < entities.length; i++) {
    const entity = entities[i];
    if (!entity || !entity.polyline) continue;

    const positions = getPolylinePositions(entity, time);
    if (!positions) continue;

    const signature = buildPolylineSignature(positions);
    if (!signature) continue;

    const id = getEntityId(entity);
    candidates.push({ entity, id, signature });
  }

  const signatureToEntities = new Map();

  for (let i = 0; i < candidates.length; i++) {
    const item = candidates[i];
    const sig = item.signature;

    let arr = signatureToEntities.get(sig);
    if (!arr) {
      arr = [];
      signatureToEntities.set(sig, arr);
    }
    arr.push(item);
  }

  const result = [];
  const seenIds = new Set();

  // Overlap groups: any signature with more than one entity
  for (const group of signatureToEntities.values()) {
    if (!group || group.length <= 1) continue;

    const idsInGroup = group.map((x) => (x.id !== undefined && x.id !== null ? String(x.id) : null)).filter(Boolean);

    const keep = group[0];
    const remove = group[1] || null;

    const keepIdStr = keep?.id !== undefined && keep?.id !== null ? String(keep.id) : "";
    const others = idsInGroup.filter((x) => x !== keepIdStr);
    const suffix = others.length > 0 ? ` (overlaps with: ${others.slice(0, 10).join(", ")}${others.length > 10 ? ", ..." : ""})` : "";

    const dedupeKey = keep?.id ?? keep?.entity;
    if (!seenIds.has(dedupeKey)) {
      seenIds.add(dedupeKey);

      result.push({
        entity: keep.entity,
        id: keep.id,
        removeEntity: remove ? remove.entity : null,
        removeId: remove ? remove.id : null,
        reasons: [`Overlapping polyline${suffix}`],
      });
    }
  }

  return result;
}
